import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { TesterConfig } from "../../config/types";
import type {
  ApiCallRecord,
  ApiRequestSpec,
  DecisionPromptSpec,
  FlowContext,
  FlowStep,
  LogEntry,
  LogLevel,
  OrderSnapshot,
  PendingDecision,
  SnapshotScope,
  PollOptions,
  RunnerStatus,
  StepRun,
  StepRuntime,
  SupplierContext,
} from "../types";
import { executeRecorded } from "./apiClient";
import {
  ApiCallError,
  FlowAbortedError,
  FlowStepError,
  PollTimeoutError,
  StepSkippedError,
} from "./errors";
import { fetchSnapshot, inferSnapshotScope, snapshotMissingScope, UI_SNAPSHOT_SCOPE } from "./orderState";
import { pollUntil } from "./poll";
import {
  getSupplierLoginSecret,
  getSupplierSession,
  proxyLoginSupplier,
  SupplierAuthError,
} from "./supplierSession";
import { stepById, stepsUntil } from "../flowGraph";
import { toHistoryEntry, useExecutionHistory } from "../../config/executionHistoryStore";

export interface RunnerState {
  status: RunnerStatus;
  targetStepId?: string;
  plannedStepIds: string[];
  /** Branch steps executed from the timeline, in order. */
  branchStepIds: string[];
  runs: Record<string, StepRun>;
  currentStepId?: string;
  failedStepId?: string;
  log: LogEntry[];
  snapshot?: OrderSnapshot;
  ctx?: FlowContext;
  startedAt?: number;
  endedAt?: number;
  decision?: PendingDecision;
  totalPollChecks: number;
  totalApiCalls: number;
  eventsProcessed: number;
  lastError?: string;
  /** True once the planned happy-path target has been reached. */
  targetReached: boolean;
}

const initialState: RunnerState = {
  status: "idle",
  plannedStepIds: [],
  branchStepIds: [],
  runs: {},
  log: [],
  totalPollChecks: 0,
  totalApiCalls: 0,
  eventsProcessed: 0,
  targetReached: false,
};

export interface StartArgs {
  config: TesterConfig;
  supplier: SupplierContext;
  orcaUser: FlowContext["orcaUser"];
  targetStepId: string;
  /** Mobile config `supplier_login_secret`. Omit to fall back to a previously saved secret. */
  supplierLoginSecret?: string;
}

const emptyRun = (stepId: string): StepRun => ({
  stepId,
  status: "pending",
  apiCalls: [],
  eventsProcessed: 0,
});

const cloneCtx = (ctx: FlowContext): FlowContext => ({
  ...ctx,
  supplier: { ...ctx.supplier },
  enquiry: ctx.enquiry ? { ...ctx.enquiry } : undefined,
  child: ctx.child ? { ...ctx.child } : undefined,
  estimate: ctx.estimate ? { ...ctx.estimate } : undefined,
  partConfirmation: ctx.partConfirmation ? { ...ctx.partConfirmation } : undefined,
  invoice: ctx.invoice ? { ...ctx.invoice } : undefined,
  shipment: ctx.shipment ? { ...ctx.shipment } : undefined,
  flags: { ...ctx.flags },
});

export const useFlowRunner = () => {
  const [state, setState] = useState<RunnerState>(initialState);
  const stateRef = useRef(state);
  stateRef.current = state;
  const ctxRef = useRef<FlowContext | null>(null);
  const abortRef = useRef<AbortController | null>(null);
  const decisionResolverRef = useRef<((optionId: string) => void) | null>(null);
  const logIdRef = useRef(0);
  const startedAtRef = useRef<number>(0);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      abortRef.current?.abort();
    };
  }, []);

  useEffect(() => {
    const entry = toHistoryEntry(state);
    if (entry) useExecutionHistory.getState().upsert(entry);
  }, [state]);

  const update = useCallback((fn: (prev: RunnerState) => RunnerState) => {
    if (!mountedRef.current) return;
    setState(fn);
  }, []);

  const patchRun = useCallback(
    (stepId: string, patch: Partial<StepRun> | ((run: StepRun) => StepRun)) => {
      update((prev) => {
        const current = prev.runs[stepId] || emptyRun(stepId);
        const next = typeof patch === "function" ? patch(current) : { ...current, ...patch };
        return { ...prev, runs: { ...prev.runs, [stepId]: next } };
      });
    },
    [update]
  );

  const log = useCallback(
    (message: string, level: LogLevel = "info", stepId?: string) => {
      const at = Date.now();
      const entry: LogEntry = {
        id: ++logIdRef.current,
        at,
        offsetMs: startedAtRef.current ? at - startedAtRef.current : 0,
        level,
        stepId,
        message,
      };
      update((prev) => ({ ...prev, log: [...prev.log, entry] }));
    },
    [update]
  );

  const syncCtx = useCallback(() => {
    if (!ctxRef.current) return;
    const snapshot = ctxRef.current.snapshot;
    const ctx = cloneCtx(ctxRef.current);
    update((prev) => ({ ...prev, ctx, snapshot: snapshot ?? prev.snapshot }));
  }, [update]);

  const buildRuntime = useCallback(
    (step: FlowStep, signal: AbortSignal, flags: { mutated: boolean; didRefresh: boolean }): StepRuntime => {
      const ctx = ctxRef.current!;
      const onRecord = (record: ApiCallRecord) => {
        patchRun(step.id, (run) => ({ ...run, apiCalls: [...run.apiCalls, record] }));
        update((prev) => ({
          ...prev,
          totalApiCalls: prev.totalApiCalls + 1,
          totalPollChecks: prev.totalPollChecks + (record.isPoll ? 1 : 0),
        }));
        if (!record.isPoll) {
          log(
            `${record.method} ${record.path} → ${record.status ?? "ERR"}${
              record.durationMs != null ? ` (${record.durationMs}ms)` : ""
            }`,
            record.errorMessage ? "error" : "debug",
            step.id
          );
        }
      };

      const api = <T = any>(spec: ApiRequestSpec) => {
        if (!spec.isPoll) flags.mutated = true;
        return executeRecorded<T>(spec, { stepId: step.id, onRecord, signal });
      };

      const refreshSnapshot = async (scope?: SnapshotScope) => {
        flags.didRefresh = true;
        const snapshot = await fetchSnapshot(ctx, api, scope);
        ctx.snapshot = snapshot;
        update((prev) => ({ ...prev, snapshot }));
        return snapshot;
      };

      const poll = async <T>(opts: PollOptions<T>): Promise<T> => {
        patchRun(step.id, { status: "waiting" });
        log(`Waiting for event: ${opts.expected}`, "info", step.id);
        try {
          return await pollUntil<T>({
            ...opts,
            signal,
            onUpdate: (record) => patchRun(step.id, { poll: record }),
          });
        } finally {
          patchRun(step.id, (run) => ({
            ...run,
            status: run.status === "waiting" ? "running" : run.status,
          }));
        }
      };

      const decide = (prompt: DecisionPromptSpec) =>
        new Promise<string>((resolve, reject) => {
          if (signal.aborted) return reject(new FlowAbortedError());
          decisionResolverRef.current = resolve;
          update((prev) => ({
            ...prev,
            status: "awaitingDecision",
            decision: { stepId: step.id, prompt },
          }));
          log(`Decision required: ${prompt.title}`, "warn", step.id);
          const onAbort = () => {
            decisionResolverRef.current = null;
            reject(new FlowAbortedError());
          };
          signal.addEventListener("abort", onAbort, { once: true });
        }).then((choice) => {
          update((prev) => ({ ...prev, status: "running", decision: undefined }));
          const logged = prompt.input ? (choice === "abort" ? "cancelled" : "secret saved") : choice;
          log(`Decision: ${logged}`, "info", step.id);
          return choice;
        });

      const getSupplierToken = async () => {
        const existing = getSupplierSession(ctx.supplier.id);
        if (existing?.token) return existing.token;
        const secret = (
          ctx.supplierLoginSecret !== undefined
            ? ctx.supplierLoginSecret
            : getSupplierLoginSecret()
        ).trim();
        if (!secret) {
          throw new SupplierAuthError(
            "supplier_login_secret is missing in mobile config.",
            "NO_SECRET"
          );
        }
        log(`No supplier session cached; performing proxy login for ${ctx.supplier.name}`, "info", step.id);
        const session = await proxyLoginSupplier({
          api,
          supplierId: ctx.supplier.id,
          mobileNumber: ctx.supplier.mobileNumber || "",
          secret,
        });
        return session.token;
      };

      return {
        ctx,
        api,
        poll,
        decide,
        log: (message, level) => log(message, level, step.id),
        refreshSnapshot,
        getSupplierToken,
        markEventProcessed: () => {
          patchRun(step.id, (run) => ({ ...run, eventsProcessed: run.eventsProcessed + 1 }));
          update((prev) => ({ ...prev, eventsProcessed: prev.eventsProcessed + 1 }));
        },
        signal,
      };
    },
    [log, patchRun, update]
  );

  /** Runs one step; returns "completed" | "skipped" | "failed" | "aborted". */
  const runStep = useCallback(
    async (step: FlowStep, signal: AbortSignal) => {
      const startedAt = Date.now();
      update((prev) => ({ ...prev, currentStepId: step.id, failedStepId: undefined }));
      patchRun(step.id, {
        status: "running",
        startedAt,
        endedAt: undefined,
        error: undefined,
        note: undefined,
        poll: undefined,
        apiCalls: [],
        eventsProcessed: 0,
      });
      log(`▶ ${step.name}`, "info", step.id);
      const flags = { mutated: false, didRefresh: false };
      const rt = buildRuntime(step, signal, flags);
      const scope = inferSnapshotScope(step, ctxRef.current!);

      const finish = (status: StepRun["status"], extra: Partial<StepRun> = {}) => {
        const endedAt = Date.now();
        patchRun(step.id, { status, endedAt, ...extra });
        return status;
      };

      try {
        if (ctxRef.current?.enquiry && snapshotMissingScope(ctxRef.current, scope)) {
          await rt.refreshSnapshot(scope);
          syncCtx();
        }
        const skipReason = step.shouldSkip?.(ctxRef.current!);
        if (skipReason) {
          log(`↷ ${step.name} skipped: ${skipReason}`, "warn", step.id);
          return finish("skipped", { note: skipReason });
        }

        flags.mutated = false;
        flags.didRefresh = false;
        await step.execute(rt);
        syncCtx();

        const reuseSnapshot = Boolean(ctxRef.current?.snapshot) && (!flags.mutated || flags.didRefresh);
        if (step.verify) {
          const snapshot = reuseSnapshot
            ? ctxRef.current!.snapshot!
            : await rt.refreshSnapshot(inferSnapshotScope(step, ctxRef.current!));
          syncCtx();
          const result = step.verify(snapshot, ctxRef.current!);
          if (!result.ok) {
            log(`✕ ${step.name}: post-condition failed (${result.actual})`, "error", step.id);
            return finish("failed", {
              error: {
                reason: "Post-condition not met after the action completed",
                expected: result.expected,
                actual: result.actual,
              },
            });
          }
        } else if (ctxRef.current?.enquiry && !reuseSnapshot) {
          await rt.refreshSnapshot(scope);
          syncCtx();
        }
        log(`✓ ${step.name} (${Date.now() - startedAt}ms)`, "success", step.id);
        return finish("completed");
      } catch (err: any) {
        syncCtx();
        if (err instanceof FlowAbortedError || err?.name === "CanceledError" || signal.aborted) {
          log(`■ ${step.name} cancelled`, "warn", step.id);
          finish("pending", { note: "Cancelled by user" });
          return "aborted" as const;
        }
        if (err instanceof StepSkippedError) {
          log(`↷ ${step.name} skipped: ${err.message}`, "warn", step.id);
          return finish("skipped", { note: err.message });
        }
        const apiCall: ApiCallRecord | undefined =
          err instanceof FlowStepError ? err.apiCall : undefined;
        const error: StepRun["error"] = {
          reason: err?.message || String(err),
          expected: err instanceof FlowStepError ? err.expected : undefined,
          actual:
            err instanceof FlowStepError
              ? err.actual ?? (apiCall ? `HTTP ${apiCall.status ?? "ERR"}: ${apiCall.errorMessage}` : undefined)
              : undefined,
          apiCallId: apiCall?.id,
        };
        if (err instanceof PollTimeoutError) {
          error.reason = err.message;
        } else if (err instanceof ApiCallError) {
          error.reason = err.message;
        }
        log(`✕ ${step.name}: ${error.reason}`, "error", step.id);
        return finish("failed", { error });
      }
    },
    [buildRuntime, log, patchRun, syncCtx, update]
  );

  const runSequence = useCallback(
    async (steps: FlowStep[], kind: "plan" | "branch" = "plan") => {
      const controller = new AbortController();
      abortRef.current = controller;
      update((prev) => ({ ...prev, status: "running", decision: undefined, endedAt: undefined }));

      for (const step of steps) {
        if (controller.signal.aborted) break;
        const outcome = await runStep(step, controller.signal);
        if (outcome === "aborted") {
          update((prev) => ({ ...prev, status: "cancelled", endedAt: Date.now(), currentStepId: undefined }));
          return "cancelled";
        }
        if (outcome === "failed") {
          update((prev) => ({
            ...prev,
            status: "failed",
            failedStepId: step.id,
            endedAt: Date.now(),
            currentStepId: undefined,
            lastError: prev.runs[step.id]?.error?.reason,
          }));
          log(kind === "plan" ? `Flow halted at "${step.name}"` : `Branch action "${step.name}" failed`, "error");
          return "failed";
        }
      }
      update((prev) => ({
        ...prev,
        status: "completed",
        endedAt: Date.now(),
        currentStepId: undefined,
        targetReached: kind === "plan" ? true : prev.targetReached,
      }));
      log(kind === "plan" ? "Target reached" : "Branch action completed", "success");
      return "completed";
    },
    [log, runStep, update]
  );

  const remainingPlanned = useCallback((fromStepId?: string) => {
    const planned = ctxRef.current ? stateRef.current.plannedStepIds : [];
    const startIdx = fromStepId ? planned.indexOf(fromStepId) : 0;
    return planned
      .slice(startIdx < 0 ? 0 : startIdx)
      .map((id) => stepById(id)!)
      .filter(Boolean);
  }, []);

  const start = useCallback(
    (args: StartArgs) => {
      abortRef.current?.abort();
      const steps = stepsUntil(args.targetStepId);
      const now = Date.now();
      startedAtRef.current = now;
      logIdRef.current = 0;
      ctxRef.current = {
        config: args.config,
        orcaUser: args.orcaUser,
        supplier: args.supplier,
        flags: {},
        supplierLoginSecret: args.supplierLoginSecret,
      };
      const runs: Record<string, StepRun> = {};
      steps.forEach((s) => (runs[s.id] = emptyRun(s.id)));
      setState({
        ...initialState,
        status: "running",
        targetStepId: args.targetStepId,
        plannedStepIds: steps.map((s) => s.id),
        runs,
        startedAt: now,
        ctx: cloneCtx(ctxRef.current),
      });
      log(
        `Starting flow → target "${stepById(args.targetStepId)?.targetLabel}" (${steps.length} steps, supplier ${
          args.supplier.name
        } / ${args.supplier.isLeanFlow ? "lean review flow" : "autonomous"})`,
        "info"
      );
      void runSequence(steps);
    },
    [log, runSequence]
  );

  const retryStep = useCallback(() => {
    const failedId = stateRef.current.failedStepId;
    if (!failedId || !ctxRef.current) return;
    const isBranch = stateRef.current.branchStepIds.includes(failedId);
    log(`Retrying "${stepById(failedId)?.name}"`, "info");
    if (isBranch) {
      void runSequence([stepById(failedId)!], "branch");
    } else {
      void runSequence(remainingPlanned(failedId));
    }
  }, [log, remainingPlanned, runSequence]);

  const skipFailedStep = useCallback(() => {
    const failedId = stateRef.current.failedStepId;
    if (!failedId || !ctxRef.current) return;
    const step = stepById(failedId);
    if (!step?.optional) return;
    patchRun(failedId, { status: "skipped", note: "Skipped by user after failure", endedAt: Date.now() });
    log(`Skipping optional step "${step.name}" and continuing`, "warn");
    const planned = stateRef.current.plannedStepIds;
    const idx = planned.indexOf(failedId);
    const rest = planned.slice(idx + 1).map((id) => stepById(id)!);
    if (!rest.length) {
      update((prev) => ({ ...prev, status: "completed", failedStepId: undefined, endedAt: Date.now() }));
      return;
    }
    update((prev) => ({ ...prev, failedStepId: undefined }));
    void runSequence(rest);
  }, [log, patchRun, runSequence, update]);

  const continueToTarget = useCallback(
    (targetStepId: string) => {
      if (!ctxRef.current) return;
      const all = stepsUntil(targetStepId).map((s) => s.id);
      const done = new Set(stateRef.current.plannedStepIds);
      const extra = all.filter((id) => !done.has(id));
      if (!extra.length) return;
      update((prev) => {
        const runs = { ...prev.runs };
        extra.forEach((id) => (runs[id] = emptyRun(id)));
        return {
          ...prev,
          targetStepId,
          plannedStepIds: [...prev.plannedStepIds, ...extra],
          runs,
          failedStepId: undefined,
          targetReached: false,
        };
      });
      log(`Extending run → target "${stepById(targetStepId)?.targetLabel}"`, "info");
      void runSequence(extra.map((id) => stepById(id)!));
    },
    [log, runSequence, update]
  );

  const runBranch = useCallback(
    (stepId: string) => {
      const step = stepById(stepId);
      if (!step || !ctxRef.current) return;
      update((prev) => ({
        ...prev,
        branchStepIds: prev.branchStepIds.includes(stepId)
          ? prev.branchStepIds
          : [...prev.branchStepIds, stepId],
        runs: { ...prev.runs, [stepId]: emptyRun(stepId) },
        failedStepId: undefined,
      }));
      log(`Branch action: ${step.name}`, "info");
      void runSequence([step], "branch");
    },
    [log, runSequence, update]
  );

  const resolveDecision = useCallback((optionId: string) => {
    const resolver = decisionResolverRef.current;
    decisionResolverRef.current = null;
    resolver?.(optionId);
  }, []);

  const cancel = useCallback(() => {
    abortRef.current?.abort();
    decisionResolverRef.current = null;
    update((prev) =>
      prev.status === "running" || prev.status === "awaitingDecision"
        ? { ...prev, status: "cancelled", endedAt: Date.now(), decision: undefined }
        : prev
    );
    log("Execution cancelled by user", "warn");
  }, [log, update]);

  const reset = useCallback(() => {
    abortRef.current?.abort();
    decisionResolverRef.current = null;
    ctxRef.current = null;
    setState(initialState);
  }, []);

  const refreshState = useCallback(async () => {
    if (!ctxRef.current?.enquiry) return;
    const api = <T = any>(spec: ApiRequestSpec) =>
      executeRecorded<T>(spec, { stepId: "manual_refresh", onRecord: () => undefined });
    const snapshot = await fetchSnapshot(ctxRef.current, api, UI_SNAPSHOT_SCOPE);
    ctxRef.current.snapshot = snapshot;
    update((prev) => ({ ...prev, snapshot }));
  }, [update]);

  const isBusy = state.status === "running" || state.status === "awaitingDecision";

  return useMemo(
    () => ({
      state,
      isBusy,
      start,
      retryStep,
      skipFailedStep,
      continueToTarget,
      runBranch,
      resolveDecision,
      cancel,
      reset,
      refreshState,
    }),
    [state, isBusy, start, retryStep, skipFailedStep, continueToTarget, runBranch, resolveDecision, cancel, reset, refreshState]
  );
};

export type FlowRunner = ReturnType<typeof useFlowRunner>;
